import request from '@/utils/request'
import store from '@/store'
import router from '@/router'
import { Message } from 'element-ui'

// 菜单路径到menu_id的映射缓存（避免每次都查询）
let menuPathMap = null
let menuPathMapPromise = null

/**
 * 初始化菜单路径映射表
 */
async function initMenuPathMap() {
  if (menuPathMap) {
    return menuPathMap
  }

  if (menuPathMapPromise) {
    return menuPathMapPromise
  }

  menuPathMapPromise = new Promise(async (resolve, reject) => {
    try {
      const response = await request({
        url: '/web/service?method=tCoreMenu_platformList',
        method: 'post'
      })
      if (response.rows && response.rows.length > 0) {
        menuPathMap = buildMenuPathMap(response.rows)
        menuPathMapPromise = null
        resolve(menuPathMap)
      } else {
        menuPathMapPromise = null
        // 菜单列表为空，提示用户没有菜单权限
        Message.error('您没有任何菜单权限，请联系管理员分配权限')
        reject(new Error('菜单列表为空'))
      }
    } catch (error) {
      menuPathMapPromise = null
      console.error('获取菜单列表失败:', error)
      reject(error)
    }
  })

  return menuPathMapPromise
}

/**
 * 构建路径映射表（包含子菜单）
 */
function buildMenuPathMap(menus) {
  const map = {}

  function traverse(menuList) {
    if (!menuList || !Array.isArray(menuList)) {
      return
    }

    menuList.forEach(menu => {
      const path = menu.menu_url || menu.href
      const menuId = menu.menu_id || menu.id

      if (path && menuId) {
        // 规范化：去掉查询串、去掉末尾斜杠
        const base = (path.split('?')[0] || '').replace(/\/+$/g, '')
        const withSlash = base.startsWith('/') ? base : '/' + base
        const withoutSlash = withSlash.substring(1)

        // 存储多种等价形式，提升命中率
        map[withSlash] = menuId
        map[withoutSlash] = menuId
        // 兼容末尾斜杠形式
        map[withSlash + '/'] = menuId
        map['/' + withoutSlash + '/'] = menuId
      }

      // 递归处理子菜单
      if (menu.children && menu.children.length > 0) {
        traverse(menu.children)
      }
    })
  }

  traverse(menus)
  return map
}

/**
 * 查找路径对应的menu_id（支持子路径匹配）
 */
function findMenuIdByPath(menuPathMap, path) {
  if (!path || !menuPathMap) {
    return null
  }

  // 去查询串并规范化末尾斜杠
  let cleanPath = path.split('?')[0]
  if (cleanPath.length > 1) {
    cleanPath = cleanPath.replace(/\/+$/g, '')
  }

  // 精确匹配
  if (menuPathMap[cleanPath]) {
    return menuPathMap[cleanPath]
  }
  // 兼容不带/形式
  const noSlash = cleanPath.startsWith('/') ? cleanPath.substring(1) : cleanPath
  if (menuPathMap[noSlash]) {
    return menuPathMap[noSlash]
  }

  // 模糊匹配（处理带参数的路由）
  // 按路径长度降序排序，优先匹配更长的路径
  const sortedPaths = Object.keys(menuPathMap).sort((a, b) => b.length - a.length)

  for (const menuPath of sortedPaths) {
    const normalizedMenuPath = menuPath.endsWith('/') && menuPath !== '/' ? menuPath.replace(/\/+$/g, '') : menuPath
    if (cleanPath.startsWith(normalizedMenuPath) || normalizedMenuPath.startsWith(cleanPath)) {
      return menuPathMap[menuPath]
    }
  }

  return null
}

/**
 * 根据路由路径获取menu_id（通过后端接口）
 */
async function getMenuIdByPathFromServer(path) {
  try {
    const response = await request({
      url: '/web/service?method=home_getMenuIdByPath',
      method: 'post',
      params: {
        path: path
      }
    })

    if (response.code === 0 && response.data && response.data.menuId) {
      return response.data.menuId
    }
    return null
  } catch (error) {
    console.error('根据路径获取菜单ID失败:', error)
    return null
  }
}

/**
 * 记录菜单访问
 */
export async function recordMenuVisit(path) {
  // 排除不需要记录的路径
  const excludePaths = ['/login', '/register', '/404', '/callScreen', '/purchaseRegister', '/supplierRegister', '/home']
  if (excludePaths.includes(path)) {
    return
  }

  const userId = store.getters['user/userId'] || JSON.parse(localStorage.getItem('userId') || 'null')

  if (!userId) {
    return
  }

  let menuId = null

  // 先尝试从缓存中获取
  try {
    await initMenuPathMap()
    menuId = findMenuIdByPath(menuPathMap, path)
  } catch (error) {
    console.warn('从缓存获取菜单ID失败，尝试从服务器获取:', error)
  }

  // 如果缓存中没有，从服务器获取
  if (!menuId) {
    menuId = await getMenuIdByPathFromServer(path)
  }

  if (!menuId) {
    // 如果找不到对应的菜单，不记录
    return
  }

  try {
    await request({
      url: '/web/service?method=home_recordMenuVisit',
      method: 'post',
      params: {
        menuId: menuId
      }
    })
  } catch (error) {
    // 静默失败，不影响用户体验
    console.error('记录菜单访问失败:', error)
  }
}

/**
 * 清除菜单路径映射缓存（用于菜单更新后刷新）
 */
export function clearMenuPathMapCache() {
  menuPathMap = null
  menuPathMapPromise = null
}


// WEBPACK FOOTER //
// ./src/utils/menu-visit.js